"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, Check, ArrowRight, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader } from "../ui/Card";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

interface AssessmentOption {
  valor: number;
  texto: string;
}

interface AssessmentQuestion {
  id: number;
  texto: string;
  opcoes: AssessmentOption[];
  dimensao: string;
  order: number;
}

interface ModernAssessmentInterfaceProps {
  questions: AssessmentQuestion[];
  currentQuestionIndex: number;
  selectedValue: number | null;
  onSelectAnswer: (value: number) => void;
  onNext: () => void;
  onPrevious: () => void;
  isNextDisabled: boolean;
  isPreviousDisabled: boolean;
  isSubmitting: boolean;
  isLastQuestion: boolean;
  themeName?: string;
}

const optionVariants = {
  initial: { opacity: 1, y: 0 },
  animate: { opacity: 1, y: 0 }
};

export function ModernAssessmentInterface({
  questions,
  currentQuestionIndex,
  selectedValue,
  onSelectAnswer,
  onNext,
  onPrevious,
  isNextDisabled,
  isPreviousDisabled,
  isSubmitting,
  isLastQuestion,
  themeName
}: ModernAssessmentInterfaceProps) {
  const [, setDirection] = useState(0);
  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;


  useEffect(() => {
    setDirection(0);
  }, [currentQuestionIndex]);

  if (!currentQuestion) return null;

  const handleNext = () => {
    setDirection(1);
    onNext();
  };

  const handlePrevious = () => {
    setDirection(-1);
    onPrevious();
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header - Fixed height */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 md:px-8 md:pt-6 md:pb-4">
        <div className="max-w-3xl mx-auto">
          {/* Progress Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <motion.div
                className="flex items-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-slate-600">
                  {themeName || "Avaliação de Perfil"}
                </span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <span className="text-sm font-medium text-slate-900">
                  {currentQuestionIndex + 1} de {questions.length}
                </span>
              </motion.div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-slate-200 rounded-full h-1.5">
              <motion.div
                className="bg-primary h-1.5 rounded-full"
                style={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Flexible height */}
      <div className="flex-1 min-h-0 px-4 md:px-8 overflow-y-auto">
        <div className="max-w-3xl mx-auto h-full flex flex-col">
          <div className="flex-1 flex flex-col justify-center py-2 md:py-4">
            {/* Question Card */}
            <Card className="border border-slate-200 shadow-sm bg-white">
              <CardHeader className="pb-3">
                <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-slate-900 leading-tight">
                  {currentQuestion.texto}
                </h1>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Answer Options */}
                <div className="space-y-2 md:space-y-3">
                  {currentQuestion.opcoes.map((option, index) => {
                    const isSelected = selectedValue === option.valor;
                    return (
                      <motion.div
                        key={option.valor}
                        custom={index}
                        variants={optionVariants}
                        initial="initial"
                        animate="animate"
                        whileTap={{ scale: 0.98 }}
                      >
                        <button
                          onClick={() => onSelectAnswer(option.valor)}
                          disabled={isSubmitting}
                          className={cn(
                            "w-full p-3 md:p-4 rounded-lg border-2 transition-all duration-200 text-left relative",
                            "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1",
                            isSelected 
                              ? "border-primary bg-primary-50" 
                              : "border-slate-200 hover:border-primary-300 hover:bg-slate-50",
                            isSubmitting && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm md:text-base font-medium flex-1 pr-4 leading-snug">
                              {option.texto}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs font-medium text-slate-500">
                                {option.valor}
                              </span>
                              <div
                                className={cn(
                                  "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
                                  isSelected 
                                    ? "border-primary bg-primary text-white" 
                                    : "border-slate-300"
                                )}
                              >
                                {isSelected && <Check className="h-3 w-3" />}
                              </div>
                            </div>
                          </div>
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer - Fixed height */}
      <div className="flex-shrink-0 px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={isPreviousDisabled}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>

            <Button
              onClick={handleNext}
              disabled={isNextDisabled}
              className={cn(
                "flex items-center gap-2",
                isSubmitting && "opacity-80"
              )}
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  Processando...
                </>
              ) : (
                <>
                  {isLastQuestion ? "Finalizar" : "Próxima"}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}